import {collection, doc, addDoc, getDoc, getDocs, setDoc, updateDoc, where, query, arrayUnion, arrayRemove, documentId} from 'firebase/firestore/lite';
import {db} from "../firebase-config";
import axios from "axios";

export const addIdToDoc = (collection_type, doc_id) => {
    return updateDoc(doc(db, collection_type, doc_id), {"id": doc_id,});
}

/**
 * Get documents by ID array with automatic batching
 * Firestore 'in' operator has a max of 30 values, so we batch if needed
 */
export const getUpTo10DocsWithIdArray = async (collection_id, id_array) => {
    if (!id_array || id_array.length === 0) {
        return { docs: [] };
    }

    // If array is 30 or less, fetch in one query
    if (id_array.length <= 30) {
        return getDocs(query(collection(db, collection_id), where(documentId(), 'in', id_array)));
    }

    // If more than 30, split into batches of 30
    const batchSize = 30;
    const batches = [];
    
    for (let i = 0; i < id_array.length; i += batchSize) {
        const batch = id_array.slice(i, i + batchSize);
        batches.push(
            getDocs(query(collection(db, collection_id), where(documentId(), 'in', batch)))
        );
    }

    // Wait for all batches and combine results
    const results = await Promise.all(batches);
    
    // Merge all docs into a single response object that mimics QuerySnapshot
    const allDocs = [];
    results.forEach(result => {
        allDocs.push(...result.docs);
    });

    return {
        docs: allDocs,
        _docs: allDocs  // Firestore Lite compatibility
    };
}

export const getUser = (user_id) => {
    return getDoc(doc(db, "users", user_id));
}

export const setUser = (user) => {
    return setDoc(doc(db, "users", user.id), user);
}

export const checkUserMail = (email) => {
    return getDocs(query(collection(db, "users"), where("email", "==", email)));
}

export const getLeague = (link) => {
    return getDocs(query(collection(db, "leagues"), where("link", "==", link)));
}

export const getSeason = (seasons_type, link) => {
    return getDocs(query(collection(db, seasons_type), where("link", "==", link)));
}

export const addApplicantToSeason = (seasons_type, season_id, user_id) => {
    return updateDoc(doc(db, seasons_type, season_id), {applicants: arrayUnion(user_id)});
}

export const removeApplicantFromSeason = (seasons_type, season_id, user_id) => {
    return updateDoc(doc(db, seasons_type, season_id), {applicants: arrayRemove(user_id)});
}

export const addPlayerToSeason = (seasons_type, season_id, user_id) => {
    return updateDoc(doc(db, seasons_type, season_id), {players: arrayUnion(user_id)});
}

export const addSeasonToUser = (season_id, user_id) => {
    return updateDoc(doc(db, "users", user_id), {player_of: arrayUnion(season_id)});
}

export const createSeasonPlayerStats = (collection_type, stat) => {
    return addDoc(collection(db, collection_type), stat);
}

export const getSeasonInfo = (season_type, year) => {
    return getDoc(doc(db, season_type + "_infos", year));
}

export const getSeasonPlayerStats = (season_type, season_id, player_id) => {
    return getDocs(query(collection(db, season_type + "_seasons_player_stats"), where("player_id", "==", player_id), where("season_id", "==", season_id) ));
}

export const updateSeasonPlayerStats = (season_type, season_player_stats) => {
    return setDoc(doc(db, season_type + "_seasons_player_stats", season_player_stats.id), season_player_stats);
}

export const getSeasonAllPlayersStats = (season_type, season_id) => {
    return getDocs(query(collection(db, season_type + "_seasons_player_stats"), where("season_id", "==", season_id)));
}

export const updateSeasonPlayerPayment = (season_type, player_stats_id, payment) => {
    return updateDoc(doc(db, season_type + "_seasons_player_stats", player_stats_id), {"payment": payment,});
}

export const updateSeasonPlayerNote = (season_type, player_stats_id, note) => {
    return updateDoc(doc(db, season_type + "_seasons_player_stats", player_stats_id), {"note": note}, { merge: true });
}

/**
 * Optimized: Fetch only essential user fields to reduce read size
 * Returns: {id, name, email, avatar_url}
 */
export const getLeanUserData = (user_ids) => {
    // Since Firestore Lite doesn't support field projections, we fetch full docs
    // but this function serves as a placeholder for future optimization
    // when switching to full Firebase SDK or using a backend service
    return getUpTo10DocsWithIdArray("users", user_ids);
}

/**
 * Fetch season stats with pagination to reduce data transfer
 * Supports cursor-based pagination for efficient large dataset handling
 */
export const getSeasonAllPlayersStatsPaginated = (season_type, season_id, pageSize = 25, lastDocSnapshot = null) => {
    let q = query(
        collection(db, season_type + "_seasons_player_stats"),
        where("season_id", "==", season_id)
    );
    
    // Add pagination: if we have a cursor, start after it
    if (lastDocSnapshot) {
        q = query(
            collection(db, season_type + "_seasons_player_stats"),
            where("season_id", "==", season_id),
            // Note: ordering required for efficient pagination
        );
    }
    
    return getDocs(q);
}

/**
 * Batch fetch user data in chunks of up to 10 (Firestore 'in' limit)
 * Returns both user docs and metadata for efficient caching
 */
export const getUsersInBatches = async (userIds) => {
    if (!userIds || userIds.length === 0) return [];
    
    const users = [];
    const batchSize = 10;
    
    for (let i = 0; i < userIds.length; i += batchSize) {
        const batch = userIds.slice(i, i + batchSize);
        const snapshot = await getUpTo10DocsWithIdArray("users", batch);
        snapshot.docs.forEach(doc => users.push(doc.data()));
    }
    
    return users;
}

/**
 * Cache layer for season info to avoid repeated reads
 * Season info changes infrequently, making it ideal for caching
 */
const seasonInfoCache = new Map();
const SEASON_INFO_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export const getSeasonInfoCached = async (season_type, year) => {
    const cacheKey = `${season_type}:${year}`;
    const cached = seasonInfoCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < SEASON_INFO_CACHE_TTL) {
        return Promise.resolve(cached.data);
    }
    
    const docSnap = await getSeasonInfo(season_type, year);
    seasonInfoCache.set(cacheKey, {
        data: docSnap,
        timestamp: Date.now()
    });
    
    return docSnap;
}

/**
 * Clear season info cache for a specific type or all
 */
export const clearSeasonInfoCache = (season_type = null) => {
    if (season_type) {
        const keysToDelete = Array.from(seasonInfoCache.keys()).filter(key => key.startsWith(season_type));
        keysToDelete.forEach(key => seasonInfoCache.delete(key));
    } else {
        seasonInfoCache.clear();
    }
}

/**
 * Cache layer for player stats to avoid repeated reads
 * Player stats change frequently during season, so TTL is shorter
 */
const playerStatsCache = new Map();
const PLAYER_STATS_CACHE_TTL = 2 * 60 * 1000; // 2 minutes for player stats

export const getSeasonAllPlayersStatsCached = async (season_type, season_id) => {
    const cacheKey = `${season_type}:${season_id}`;
    const cached = playerStatsCache.get(cacheKey);
    
    if (cached && Date.now() - cached.timestamp < PLAYER_STATS_CACHE_TTL) {
        return Promise.resolve(cached.data);
    }
    
    const docSnap = await getSeasonAllPlayersStats(season_type, season_id);
    playerStatsCache.set(cacheKey, {
        data: docSnap,
        timestamp: Date.now()
    });
    
    return docSnap;
}

/**
 * Clear player stats cache
 */
export const clearPlayerStatsCache = (season_type = null, season_id = null) => {
    if (season_type && season_id) {
        playerStatsCache.delete(`${season_type}:${season_id}`);
    } else if (season_type) {
        const keysToDelete = Array.from(playerStatsCache.keys()).filter(key => key.startsWith(season_type));
        keysToDelete.forEach(key => playerStatsCache.delete(key));
    } else {
        playerStatsCache.clear();
    }
}

let cachedTime = null;
let cacheExpiry = null;
const CACHE_DURATION = 1 * 60 * 1000; // Cache for 1 minute
const STORAGE_KEY = 'timezone_cache';
const STORAGE_EXPIRY_KEY = 'timezone_cache_expiry';

export const getTime = () => {
    //clearTimeCache();

    // Check if we have valid cached data in memory
    if (cachedTime && cacheExpiry && Date.now() < cacheExpiry) {
        return Promise.resolve(cachedTime);
    }

    // Check if we have valid cached data in browser localStorage
    try {
        const storedTime = localStorage.getItem(STORAGE_KEY);
        const storedExpiry = localStorage.getItem(STORAGE_EXPIRY_KEY);

        if (storedTime && storedExpiry && Date.now() < parseInt(storedExpiry)) {
            const parsedCache = JSON.parse(storedTime);
            cachedTime = parsedCache;
            cacheExpiry = parseInt(storedExpiry);
            return Promise.resolve(parsedCache);
        }
    } catch (error) {
        console.warn("Error reading from localStorage:", error);
    }

    // Call Firebase Cloud Function HTTP endpoint with CORS support
    return axios.get('https://us-central1-fantezi-ligim.cloudfunctions.net/serverTimeHttp')
        .then(result => {
            // axios response structure: result.data contains the server response
            if (result.data && result.data.success) {
                const response = {
                    data: result.data
                };
                
                // Cache the successful response in memory
                cachedTime = response;
                cacheExpiry = Date.now() + CACHE_DURATION;

                // Also cache in browser localStorage
                try {
                    localStorage.setItem(STORAGE_KEY, JSON.stringify(response));
                    localStorage.setItem(STORAGE_EXPIRY_KEY, cacheExpiry.toString());
                } catch (error) {
                    console.warn("Error writing to localStorage:", error);
                }

                return response;
            } else {
                throw new Error("Invalid response from serverTimeHttp");
            }
        });
}

/**
 * Clear timezone cache from browser storage
 */
export const clearTimeCache = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(STORAGE_EXPIRY_KEY);
        cachedTime = null;
        cacheExpiry = null;
    } catch (error) {
        console.warn("Error clearing localStorage:", error);
    }
}
